import React from 'react';
import { CreateMilestoneInput } from '../../types/contract';
import { Button } from '../UI/Button';
import { Plus, Trash2, Calendar, DollarSign, AlertCircle } from 'lucide-react';

interface MilestoneEditorProps {
    milestones: CreateMilestoneInput[];
    onChange: (milestones: CreateMilestoneInput[]) => void;
    currency: string;
    readOnly?: boolean;
    disabledMilestoneIndices?: number[]; // Indices of milestones that cannot be removed/edited fully
}

export const MilestoneEditor: React.FC<MilestoneEditorProps> = ({
    milestones,
    onChange,
    currency,
    readOnly = false,
    disabledMilestoneIndices = [],
}) => {
    const handleAddMilestone = () => {
        const newMilestone: CreateMilestoneInput = {
            title: '',
            description: '',
            order_index: milestones.length + 1,
            payment_amount: 0,
            requires_deliverable: true,
            has_penalty: false,
            penalty_type: 'DAILY',
            penalty_value: 0,
            penalty_description: '',
            estimated_completion_date: Date.now() + 7 * 24 * 60 * 60 * 1000, // Default 7 days later
        };
        onChange([...milestones, newMilestone]);
    };

    const handleRemoveMilestone = (index: number) => {
        if (disabledMilestoneIndices.includes(index)) return;
        const newMilestones = milestones.filter((_, i) => i !== index);
        // Re-index
        const reIndexed = newMilestones.map((m, i) => ({ ...m, order_index: i + 1 }));
        onChange(reIndexed);
    };

    const handleChange = (index: number, field: keyof CreateMilestoneInput, value: any) => {
        const newMilestones = [...milestones];
        newMilestones[index] = { ...newMilestones[index], [field]: value };
        onChange(newMilestones);
    };

    // Helper to safely format dates for input[type="date"]
    const formatDateForInput = (timestamp: number) => {
        try {
            return new Date(timestamp).toISOString().split('T')[0];
        } catch (e) {
            return new Date().toISOString().split('T')[0];
        }
    };

    return (
        <div className="space-y-6">
            {milestones.map((milestone, index) => {
                const isDisabled = disabledMilestoneIndices.includes(index) || readOnly;

                return (
                    <div key={index} className="bg-white p-6 rounded-xl border border-slate-200 relative">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-slate-100 text-slate-600 text-xs font-semibold">
                                    {index + 1}
                                </span>
                                <span className="font-medium text-slate-900">里程碑 #{index + 1}</span>
                            </div>
                            {!isDisabled && (
                                <button
                                    onClick={() => handleRemoveMilestone(index)}
                                    className="text-slate-400 hover:text-red-500 transition-colors"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <div className="col-span-2">
                                <label className="block text-sm font-medium text-slate-700 mb-1">标题 <span className="text-red-500">*</span></label>
                                <input
                                    type="text"
                                    value={milestone.title}
                                    onChange={(e) => handleChange(index, 'title', e.target.value)}
                                    disabled={isDisabled}
                                    className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                                    placeholder="例如：初稿交付"
                                />
                            </div>

                            <div className="col-span-2">
                                <label className="block text-sm font-medium text-slate-700 mb-1">描述 <span className="text-red-500">*</span></label>
                                <textarea
                                    value={milestone.description}
                                    onChange={(e) => handleChange(index, 'description', e.target.value)}
                                    disabled={isDisabled}
                                    rows={2}
                                    className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500 resize-none"
                                    placeholder="描述该里程碑的具体交付内容"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">截止日期 <span className="text-red-500">*</span></label>
                                <div className="relative">
                                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                    <input
                                        type="date"
                                        value={formatDateForInput(milestone.estimated_completion_date)}
                                        onChange={(e) => handleChange(index, 'estimated_completion_date', new Date(e.target.value).getTime())}
                                        disabled={isDisabled} // Often we allow extending dates even for active milestones, but let's stick to simple logic for now
                                        className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">付款金额 ({currency}) <span className="text-red-500">*</span></label>
                                <div className="relative">
                                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                    <input
                                        type="number"
                                        value={milestone.payment_amount || ''}
                                        onChange={(e) => handleChange(index, 'payment_amount', e.target.value === '' ? 0 : parseFloat(e.target.value))}
                                        disabled={isDisabled}
                                        className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Config Toggles */}
                        <div className="flex items-center gap-6 pb-4 border-b border-slate-100 mb-4">
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={milestone.requires_deliverable}
                                    onChange={(e) => handleChange(index, 'requires_deliverable', e.target.checked)}
                                    disabled={isDisabled}
                                    className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-sm text-slate-700">需要交付物</span>
                            </label>

                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={milestone.has_penalty || false}
                                    onChange={(e) => handleChange(index, 'has_penalty', e.target.checked)}
                                    disabled={isDisabled}
                                    className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-sm text-slate-700">启用逾期惩罚</span>
                            </label>
                        </div>

                        {/* Penalty Section */}
                        {milestone.has_penalty && (
                            <div className="bg-amber-50 rounded-lg p-4 animate-in fade-in slide-in-from-top-2 duration-200">
                                <div className="flex items-center gap-2 mb-3 text-amber-800">
                                    <AlertCircle className="w-4 h-4" />
                                    <span className="text-sm font-medium">逾期惩罚设置</span>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-medium text-amber-800 mb-1">惩罚类型</label>
                                        <div className="flex rounded-md shadow-sm">
                                            <button
                                                type="button"
                                                onClick={() => !isDisabled && handleChange(index, 'penalty_type', 'DAILY')}
                                                className={`flex-1 px-3 py-1.5 text-sm border rounded-l-md ${milestone.penalty_type === 'DAILY'
                                                    ? 'bg-amber-600 text-white border-amber-600'
                                                    : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                                                    }`}
                                                disabled={isDisabled}
                                            >
                                                按天比例
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => !isDisabled && handleChange(index, 'penalty_type', 'FIXED')}
                                                className={`flex-1 px-3 py-1.5 text-sm border border-l-0 rounded-r-md ${milestone.penalty_type === 'FIXED'
                                                    ? 'bg-amber-600 text-white border-amber-600'
                                                    : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                                                    }`}
                                                disabled={isDisabled}
                                            >
                                                固定金额
                                            </button>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-xs font-medium text-amber-800 mb-1">
                                            {milestone.penalty_type === 'DAILY' ? '每日扣除比例 (%)' : `扣除金额 (${currency})`}
                                        </label>
                                        <input
                                            type="number"
                                            value={milestone.penalty_value ?? ''}
                                            onChange={(e) => {
                                                const val = e.target.value === '' ? undefined : parseFloat(e.target.value);
                                                handleChange(index, 'penalty_value', val);
                                            }}
                                            disabled={isDisabled}
                                            className={`w-full px-3 py-1.5 rounded-md border focus:outline-none focus:ring-2 focus:ring-amber-200 focus:border-amber-500 ${(milestone.penalty_type === 'FIXED' && (milestone.penalty_value || 0) > milestone.payment_amount) ||
                                                (milestone.penalty_type === 'DAILY' && (milestone.penalty_value || 0) > 100)
                                                ? 'border-red-500 bg-red-50'
                                                : 'border-slate-200'
                                                }`}
                                        />
                                        {milestone.penalty_type === 'FIXED' && (milestone.penalty_value || 0) > milestone.payment_amount && (
                                            <p className="mt-1 text-xs text-red-600">
                                                固定惩罚金额不能超过里程碑总金额 ({milestone.payment_amount})
                                            </p>
                                        )}
                                        {milestone.penalty_type === 'DAILY' && (milestone.penalty_value || 0) > 100 && (
                                            <p className="mt-1 text-xs text-red-600">
                                                每日扣除比例不能超过 100%
                                            </p>
                                        )}
                                    </div>

                                    <div className="col-span-2">
                                        <label className="block text-xs font-medium text-amber-800 mb-1">惩罚说明 (可选)</label>
                                        <input
                                            type="text"
                                            value={milestone.penalty_description || ''}
                                            onChange={(e) => handleChange(index, 'penalty_description', e.target.value)}
                                            disabled={isDisabled}
                                            className="w-full px-3 py-1.5 rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-amber-200 focus:border-amber-500"
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                );
            })}

            {!readOnly && (
                <Button
                    type="button"
                    variant="outline"
                    onClick={handleAddMilestone}
                    className="w-full py-4 border-dashed border-2 hover:border-blue-500 hover:text-blue-600 hover:bg-blue-50"
                >
                    <Plus className="w-5 h-5 mr-2" />
                    添加里程碑
                </Button>
            )}
        </div>
    );
};
